import React, { useState, useRef } from 'react';

const ImageUpload = ({ onImagesChange, maxImages = 3 }) => {
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select only image files');
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size must be less than 10MB');
    }
    
    return true;
  };

  const processFiles = async (fileList) => {
    if (images.length + fileList.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images`);
      return;
    }

    setUploading(true);
    try {
      const newImages = [];
      const newPreviews = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        validateFile(file);
        
        const base64 = await convertToBase64(file);
        const previewUrl = URL.createObjectURL(file);
        
        newImages.push({
          file,
          base64,
          name: file.name,
          size: file.size,
        });
        
        newPreviews.push(previewUrl);
      }

      const updatedImages = [...images, ...newImages];
      const updatedPreviews = [...previews, ...newPreviews];

      setImages(updatedImages);
      setPreviews(updatedPreviews);
      
      // Pass the base64 strings to parent component
      const base64Images = updatedImages.map(img => img.base64);
      onImagesChange(base64Images);
      
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const removeImage = (index) => {
    // Revoke the preview URL to free memory
    URL.revokeObjectURL(previews[index]);
    
    const updatedImages = images.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    
    setImages(updatedImages);
    setPreviews(updatedPreviews);
    
    // Update parent component
    const base64Images = updatedImages.map(img => img.base64);
    onImagesChange(base64Images);
  };

  const clearAll = () => {
    // Revoke all preview URLs
    previews.forEach(url => URL.revokeObjectURL(url));
    
    setImages([]);
    setPreviews([]);
    onImagesChange([]);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-4">
      <label className="form-label fw-bold">
        Photos ({images.length}/{maxImages})
      </label>
      
      <div
        className={`upload-area ${dragOver ? 'dragover' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{ cursor: 'pointer' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploading || images.length >= maxImages}
        />
        
        {uploading ? (
          <div>
            <div className="loading-spinner"></div>
            <p className="mt-2 mb-0">Processing images...</p>
          </div>
        ) : (
          <div>
            <i className="bi bi-cloud-upload fs-1 text-primary"></i>
            <p className="mt-2 mb-1">
              Click to select images or drag and drop
            </p>
            <small className="text-muted">
              Supports JPG, PNG, GIF up to 10MB each
            </small>
          </div>
        )}
      </div>

      {images.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Selected Images:</small>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={clearAll}
            >
              Clear All
            </button>
          </div>
          
          <div className="row">
            {images.map((image, index) => (
              <div key={index} className="col-md-4 mb-3">
                <div className="card">
                  <img
                    src={previews[index]}
                    alt={`Preview ${index + 1}`}
                    className="card-img-top image-preview"
                    style={{ height: '150px', objectFit: 'cover' }}
                  />
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted text-truncate">
                        {image.name}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </button>
                    </div>
                    <small className="text-muted">
                      {(image.size / 1024 / 1024).toFixed(2)} MB
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {images.length === 0 && (
        <div className="alert alert-info mt-3">
          <small>
            <strong>Note:</strong> All three photos are required for the API request.
          </small>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
